// src/Hooks/usePromoNewsfeedManage.js

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  collection,
  onSnapshot,
  doc,
  writeBatch,
  getDocs,
  getDoc,
  deleteField,
} from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { db, storage } from '../Firebase/firebase';

export function usePromoNewsfeedManage() {
  const collectionName = 'newsPortfolio';

  const [activeAction, setActiveAction] = useState('VIEW');
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [focusedRowId, setFocusedRowId] = useState(null);
  const [dbRows, setDbRows] = useState([]);
  const [newRows, setNewRows] = useState([]);
  const [edits, setEdits] = useState({});
  const [categories, setCategories] = useState([]);
  const [regions, setRegions] = useState([]);
  const [excelImports, setExcelImports] = useState([]);
  const [excelArchives, setExcelArchives] = useState([]);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [showUnsavedAlert, setShowUnsavedAlert] = useState(false);

  const [textFormatting, setTextFormatting] = useState({ makeUppercase: false });
  const [copiedData, setCopiedData] = useState(null);

  useEffect(() => {
    setSelectedIds([]);
    setNewRows([]);
    setEdits({});
    setFocusedRowId(null);
    setActiveAction('VIEW');
    setLoading(true);
  }, []);

  useEffect(() => {
    const unsubMain = onSnapshot(
      collection(db, collectionName),
      (snapshot) => {
        const fetchedData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        fetchedData.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setDbRows(fetchedData);
        setLoading(false);
      },
      (error) => {
        console.error(`Error fetching ${collectionName}:`, error);
        setLoading(false);
      },
    );

    const unsubCategories = onSnapshot(
      collection(db, 'categories'),
      (snapshot) => {
        const fetchedCats = snapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .filter((cat) => !cat.isArchived);
        fetchedCats.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setCategories(fetchedCats);
      },
      (error) => console.error('Error fetching categories:', error),
    );

    const unsubRegions = onSnapshot(
      collection(db, 'region'),
      (snapshot) => {
        const fetchedRegions = snapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .filter((reg) => !reg.isArchived);
        fetchedRegions.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setRegions(fetchedRegions);
      },
      (error) => console.error('Error fetching regions:', error),
    );

    const unsubExcelImports = onSnapshot(
      collection(db, 'excelImports'),
      (snapshot) => {
        const fetchedImports = snapshot.docs.map((doc) => ({
          id: doc.id,
          filename: doc.data().filename || doc.id,
          ...doc.data(),
        }));
        setExcelImports(fetchedImports);
      },
      (error) => console.error('Error fetching excelImports:', error),
    );

    const unsubExcelArchives = onSnapshot(
      collection(db, 'excelArchives'),
      (snapshot) => {
        const fetchedArchives = snapshot.docs.map((doc) => ({
          id: doc.id,
          filename: doc.data().filename || doc.id,
          ...doc.data(),
        }));
        setExcelArchives(fetchedArchives);
      },
      (error) => console.error('Error fetching excelArchives:', error),
    );

    const unsubFormat = onSnapshot(
      doc(db, 'settings', 'TextFormatting'),
      (docSnap) => {
        if (docSnap.exists()) {
          setTextFormatting({ makeUppercase: docSnap.data().makeUppercase === true });
        } else {
          setTextFormatting({ makeUppercase: false });
        }
      },
      (error) => console.error('Error fetching TextFormatting:', error),
    );

    return () => {
      unsubMain();
      unsubCategories();
      unsubRegions();
      unsubFormat();
      unsubExcelImports();
      unsubExcelArchives();
    };
  }, [collectionName]);

  const allBatches = useMemo(() => {
    return [...excelImports, ...excelArchives].sort((a, b) => {
      const getMs = (item) => {
        const d = item.importDate;
        if (!d) return 0;
        if (typeof d.toMillis === 'function') return d.toMillis();
        if (d.seconds) return d.seconds * 1000;
        return new Date(d).getTime() || 0;
      };
      return getMs(b) - getMs(a);
    });
  }, [excelImports, excelArchives]);

  const displayRows = useMemo(() => {
    const mergedDbRows = dbRows.map((row) => {
      if (edits[row.id]) {
        return { ...row, ...edits[row.id] };
      }
      return row;
    });
    const newRowIds = new Set(newRows.map((r) => r.id));
    const finalDbRows = mergedDbRows.filter((r) => !newRowIds.has(r.id));
    const combined = [...finalDbRows, ...newRows];

    if (!focusedRowId) {
      combined.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }

    return combined;
  }, [dbRows, edits, newRows, focusedRowId]);

  const checkIsDirty = useCallback(() => {
    return newRows.length > 0 || Object.keys(edits).length > 0;
  }, [newRows, edits]);

  const handleCheckboxChange = (id) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) return [];
      return [id];
    });
  };

  const handleSelectAll = (checked) => {
    setSelectedIds([]);
  };

  const handleFieldChange = (id, field, val, e = null) => {
    if (activeAction === 'VIEW') setActiveAction('EDIT_MODE');

    let finalVal = val;

    if (textFormatting?.makeUppercase && e && e.nativeEvent) {
      const inputType = e.nativeEvent.inputType;
      const data = e.nativeEvent.data;

      if (inputType === 'insertFromPaste') {
        const target = e.target;
        const cursor = target.selectionStart;
        finalVal = typeof finalVal === 'string' ? finalVal.toUpperCase() : finalVal;

        setTimeout(() => {
          if (target) target.setSelectionRange(cursor, cursor);
        }, 0);
      } else if (inputType === 'insertText' && data && typeof finalVal === 'string') {
        const target = e.target;
        const cursor = target.selectionStart;
        const dataLength = data.length;

        if (cursor !== null && cursor >= dataLength) {
          const insertedStr = finalVal.slice(cursor - dataLength, cursor);
          let swapped = '';
          for (let i = 0; i < insertedStr.length; i++) {
            const char = insertedStr[i];
            swapped +=
              char === char.toLowerCase() ? char.toUpperCase() : char.toLowerCase();
          }
          finalVal =
            finalVal.slice(0, cursor - dataLength) + swapped + finalVal.slice(cursor);

          setTimeout(() => {
            if (target) target.setSelectionRange(cursor, cursor);
          }, 0);
        }
      }
    }

    const isNewRow = newRows.some((r) => r.id === id);
    if (isNewRow) {
      setNewRows((prev) =>
        prev.map((row) => {
          if (row.id === id) {
            const updated = { ...row, [field]: finalVal };
            if (field !== 'name') {
              updated.pdfDownloadUrl = null;
              updated.pdfStoragePath = null;
              updated.pdfThumbnailUrl = null;
              updated.thumbStoragePath = null;
              updated.hiresDownloadUrl = null;
              updated.hiresStoragePath = null;
              updated.emailPreviewHtml = null;
              updated.PublishedDate = null;
            }
            return updated;
          }
          return row;
        }),
      );
    } else {
      setEdits((prev) => {
        const currentEdits = prev[id] || {};
        const newEdits = { ...currentEdits, [field]: finalVal };

        const ignoredKeys = [
          'pdfDownloadUrl',
          'pdfStoragePath',
          'pdfThumbnailUrl',
          'thumbStoragePath',
          'hiresDownloadUrl',
          'hiresStoragePath',
          'emailPreviewHtml',
          'PublishedDate',
        ];

        const hasCriticalEdits = Object.keys(newEdits).some(
          (k) => k !== 'name' && !ignoredKeys.includes(k),
        );

        if (hasCriticalEdits) {
          newEdits.pdfDownloadUrl = null;
          newEdits.pdfStoragePath = null;
          newEdits.pdfThumbnailUrl = null;
          newEdits.thumbStoragePath = null;
          newEdits.hiresDownloadUrl = null;
          newEdits.hiresStoragePath = null;
          newEdits.emailPreviewHtml = null;
          newEdits.PublishedDate = null;
        } else {
          delete newEdits.pdfDownloadUrl;
          delete newEdits.pdfStoragePath;
          delete newEdits.pdfThumbnailUrl;
          delete newEdits.thumbStoragePath;
          delete newEdits.hiresDownloadUrl;
          delete newEdits.hiresStoragePath;
          delete newEdits.emailPreviewHtml;
          delete newEdits.PublishedDate;
        }

        return { ...prev, [id]: newEdits };
      });
    }
  };

  const handleClearFields = (id) => {
    if (activeAction === 'VIEW') setActiveAction('EDIT_MODE');
    const emptyFields = {
      dateRangeType: 'ALL',
      dateFrom: '',
      dateTo: '',
      categoryId: '',
      categoryIds: [],
      regionId: '',
      regionIds: [],
      keywords: '',
      infoFilterA: '',
      infoFilterB: '',
      Batch: [],
      photoDimension: 50,
      maxResults: 20,
      includeHeader: true,
      salutation: '',
      emailClose: '',
      subjectTitle: '',
      hilightColor: '#F5F5F5',
      hilightOpacity: 100,
      pdfDownloadUrl: null,
      pdfStoragePath: null,
      pdfThumbnailUrl: null,
      thumbStoragePath: null,
      hiresDownloadUrl: null,
      hiresStoragePath: null,
      emailPreviewHtml: null,
      PublishedDate: null,
    };
    const isNewRow = newRows.some((r) => r.id === id);
    if (isNewRow) {
      setNewRows((prev) =>
        prev.map((row) => (row.id === id ? { ...row, ...emptyFields } : row)),
      );
    } else {
      setEdits((prev) => ({
        ...prev,
        [id]: { ...(prev[id] || {}), ...emptyFields },
      }));
    }
  };

  const handleAddNew = () => {
    const newId = Date.now().toString();
    const newRow = {
      id: newId,
      name: '',
      subjectTitle: '',
      dateRangeType: 'ALL',
      dateFrom: '',
      dateTo: '',
      categoryId: '',
      categoryIds: [],
      regionId: '',
      regionIds: [],
      keywords: '',
      infoFilterA: '',
      infoFilterB: '',
      Batch: [],
      photoDimension: 50,
      maxResults: 20,
      includeHeader: true,
      salutation: '',
      emailClose: '',
      hilightColor: '#F5F5F5',
      hilightOpacity: 100,
    };
    setNewRows((prev) => [...prev, newRow]);
    setActiveAction('EDIT_MODE');
    setFocusedRowId(newId);
    setSelectedIds([newId]);
  };

  const handleDeleteRequest = () => {
    if (selectedIds.length > 0) {
      setShowDeleteAlert(true);
    }
  };

  const executeDelete = async () => {
    const localIds = [];
    const dbIds = [];
    selectedIds.forEach((id) => {
      if (newRows.some((r) => r.id === id)) localIds.push(id);
      else dbIds.push(id);
    });

    if (localIds.length > 0) {
      setNewRows((prev) => prev.filter((r) => !localIds.includes(r.id)));
    }

    if (dbIds.length > 0) {
      try {
        const batch = writeBatch(db);

        for (const id of dbIds) {
          const docRef = doc(db, collectionName, id);

          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = docSnap.data();

            if (data.pdfStoragePath) {
              try {
                await deleteObject(ref(storage, data.pdfStoragePath));
              } catch (e) {
                if (e.code !== 'storage/object-not-found')
                  console.error('Error deleting PDF:', e);
              }
            } else if (data.pdfDownloadUrl) {
              try {
                await deleteObject(ref(storage, data.pdfDownloadUrl));
              } catch (e) {
                if (e.code !== 'storage/object-not-found')
                  console.error('Error deleting PDF URL:', e);
              }
            }

            if (data.thumbStoragePath) {
              try {
                await deleteObject(ref(storage, data.thumbStoragePath));
              } catch (e) {
                if (e.code !== 'storage/object-not-found')
                  console.error('Error deleting Thumb:', e);
              }
            } else if (data.pdfThumbnailUrl) {
              try {
                await deleteObject(ref(storage, data.pdfThumbnailUrl));
              } catch (e) {
                if (e.code !== 'storage/object-not-found')
                  console.error('Error deleting Thumb URL:', e);
              }
            }

            if (data.hiresStoragePath) {
              try {
                await deleteObject(ref(storage, data.hiresStoragePath));
              } catch (e) {
                if (e.code !== 'storage/object-not-found')
                  console.error('Error deleting Hi-Res:', e);
              }
            } else if (data.hiresDownloadUrl) {
              try {
                await deleteObject(ref(storage, data.hiresDownloadUrl));
              } catch (e) {
                if (e.code !== 'storage/object-not-found')
                  console.error('Error deleting Hi-Res URL:', e);
              }
            }
          }

          batch.delete(docRef);
        }

        await batch.commit();

        setEdits((prev) => {
          const next = { ...prev };
          dbIds.forEach((id) => delete next[id]);
          return next;
        });
      } catch (error) {
        console.error('Error deleting docs:', error);
      }
    }
    setSelectedIds([]);
    setShowDeleteAlert(false);
  };

  const handleSave = async () => {
    if (!checkIsDirty()) return;
    try {
      const batch = writeBatch(db);

      newRows.forEach((row) => {
        const docRef = doc(db, collectionName, row.id);
        batch.set(docRef, {
          name: row.name || '',
          subjectTitle: row.subjectTitle || '',
          dateRangeType: row.dateRangeType || 'ALL',
          dateFrom: row.dateFrom || '',
          dateTo: row.dateTo || '',
          categoryId: row.categoryId || '',
          categoryIds: row.categoryIds || [],
          regionId: row.regionId || '',
          regionIds: row.regionIds || [],
          keywords: row.keywords || '',
          infoFilterA: row.infoFilterA || '',
          infoFilterB: row.infoFilterB || '',
          Batch: row.Batch || [],
          photoDimension: row.photoDimension || 50,
          maxResults: row.maxResults || 20,
          includeHeader: row.includeHeader ?? true,
          salutation: row.salutation || '',
          emailClose: row.emailClose || '',
          hilightColor: row.hilightColor || '#F5F5F5',
          hilightOpacity:
            row.hilightOpacity !== undefined ? Number(row.hilightOpacity) : 100,
        });
      });

      const deletePromises = [];

      Object.keys(edits).forEach((id) => {
        const docRef = doc(db, collectionName, id);
        const updates = { ...edits[id] };

        if (updates.pdfDownloadUrl === null) {
          const originalRow = dbRows.find((r) => r.id === id);
          if (originalRow) {
            if (originalRow.pdfStoragePath)
              deletePromises.push(
                deleteObject(ref(storage, originalRow.pdfStoragePath)).catch((e) => {
                  if (e.code !== 'storage/object-not-found') console.error(e);
                }),
              );
            if (originalRow.thumbStoragePath)
              deletePromises.push(
                deleteObject(ref(storage, originalRow.thumbStoragePath)).catch((e) => {
                  if (e.code !== 'storage/object-not-found') console.error(e);
                }),
              );
            if (originalRow.hiresStoragePath)
              deletePromises.push(
                deleteObject(ref(storage, originalRow.hiresStoragePath)).catch((e) => {
                  if (e.code !== 'storage/object-not-found') console.error(e);
                }),
              );
          }
          updates.pdfDownloadUrl = deleteField();
          updates.pdfStoragePath = deleteField();
          updates.pdfThumbnailUrl = deleteField();
          updates.thumbStoragePath = deleteField();
          updates.hiresDownloadUrl = deleteField();
          updates.hiresStoragePath = deleteField();
          updates.emailPreviewHtml = deleteField();
          updates.PublishedDate = deleteField();
        }

        batch.update(docRef, updates);
      });

      await batch.commit();

      if (deletePromises.length > 0) {
        await Promise.all(deletePromises);
      }

      setNewRows([]);
      setEdits({});
      setActiveAction('VIEW');
    } catch (error) {
      console.error('Error saving batch:', error);
    }
  };

  const resetInterface = () => {
    setNewRows([]);
    setEdits({});
    setSelectedIds([]);
    setShowUnsavedAlert(false);
    setActiveAction('VIEW');
  };

  const fetchUniqueKeywords = async () => {
    try {
      const markersRef = collection(db, 'markerGroups');
      const snapshot = await getDocs(markersRef);
      const wordsSet = new Set();
      snapshot.forEach((doc) => {
        const data = doc.data();
        if (data.keywords) {
          const words = data.keywords
            .split(/[\s,]+/)
            .map((w) => w.trim().toUpperCase())
            .filter((w) => w.length > 0);

          words.forEach((w) => wordsSet.add(w));
        }
      });
      return Array.from(wordsSet).sort();
    } catch (error) {
      console.error('Error fetching keywords:', error);
      return [];
    }
  };

  const handleCopy = () => {
    if (selectedIds.length !== 1) return;
    const targetId = selectedIds[0];
    const isNewRow = newRows.find((r) => r.id === targetId);
    const dbRow = dbRows.find((r) => r.id === targetId);
    const edit = edits[targetId] || {};

    const combined = { ...(dbRow || {}), ...(isNewRow || {}), ...edit };

    // Explicitly exclude historical tracking and generated file URLs from the copy payload
    // Keeping markerCount so the user knows roughly the size of the document they are copying
    const {
      id,
      pdfDownloadUrl,
      pdfStoragePath,
      pdfThumbnailUrl,
      thumbStoragePath,
      hiresDownloadUrl,
      hiresStoragePath,
      emailPreviewHtml,
      PublishedDate,
      hitCount,
      broadcastCount,
      ...dataToCopy
    } = combined;

    setCopiedData(dataToCopy);
  };

  const handlePaste = async () => {
    if (!copiedData) return;
    try {
      const newId = Date.now().toString();
      const newDocRef = doc(db, collectionName, newId);
      const newData = {
        ...copiedData,
        name: copiedData.name ? `${copiedData.name} (Copy)` : 'Copy',
      };
      const batch = writeBatch(db);
      batch.set(newDocRef, newData);
      await batch.commit();
      setSelectedIds([newId]);
      setFocusedRowId(newId);
    } catch (error) {
      console.error('Error pasting row:', error);
    }
  };

  return {
    state: {
      displayRows,
      selectedIds,
      loading,
      focusedRowId,
      categories,
      regions,
      allBatches,
    },
    flags: {
      isDirty: checkIsDirty(),
      canDelete: selectedIds.length > 0,
      canSave: checkIsDirty(),
    },
    alerts: {
      showDelete: showDeleteAlert,
      setShowDelete: setShowDeleteAlert,
      showUnsaved: showUnsavedAlert,
      setShowUnsaved: setShowUnsavedAlert,
    },
    actions: {
      handleAddNew,
      handleDeleteRequest,
      handleConfirmDelete: executeDelete,
      handleClearFields,
      handleSave,
      handleCheckboxChange,
      handleSelectAll,
      handleFieldChange,
      setFocusedRowId,
      resetInterface,
      fetchUniqueKeywords,
      handleCopy,
      handlePaste,
      hasCopiedData: !!copiedData,
    },
  };
}
